import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Check, Link2, UserCheck, RefreshCw, KeyRound } from "lucide-react";
import { API, useAuth } from "../../App";

// Admin-only bar on artist/business/org pages: shows whether the owner has a verified account; if not, mint + copy a one-tap invite.
export const AdminInviteBar = ({ kind, slug }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState("");
  const [key, setKey] = useState(null);

  const load = () => axios.get(`${API}/admin/invites/status`, { params: { kind, slug }, withCredentials: true }).then((r) => { setSt(r.data); setEmail(r.data.invite?.email || r.data.known_emails?.[0] || ""); }).catch(() => setSt(null));
  useEffect(() => { if (isAdmin && slug) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [isAdmin, kind, slug]);

  if (!isAdmin || !st) return null;

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); setCopied(true); toast.success("Invite copied — paste it into your message"); setTimeout(() => setCopied(false), 2500); }
    catch { toast.message(text); }
  };
  const mint = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/invites`, { kind, slug, email: email || null }, { withCredentials: true });
      setSt((s) => ({ ...s, invite: r.data }));
      await copy(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't create invite"); }
    finally { setBusy(false); }
  };
  const mintKey = async () => {
    if (st.key_owner && !window.confirm(`${st.name} already has an access key. Issue a NEW one? The old key stops working.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/invites`, { kind, slug, access_key: true }, { withCredentials: true });
      setKey(r.data);
      await copy(r.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't issue a key"); }
    finally { setBusy(false); }
  };
  const keyRow = (
    <>
      {key ? (
        <span className="inline-flex items-center gap-2 rounded-full bg-[#54E3FF]/15 border border-[#54E3FF]/50 px-3 h-8" data-testid="admin-invite-key">
          <KeyRound className="h-3.5 w-3.5 text-[#54E3FF]" /> <span className="font-mono tracking-wider">{key.access_key}</span>
          <button type="button" onClick={() => copy(key.message)} className="underline text-[#54E3FF]" data-testid="admin-invite-key-copy">copy message</button>
        </span>
      ) : (
        <button type="button" onClick={mintKey} disabled={busy} className="inline-flex items-center gap-1.5 h-8 rounded-full border border-[#54E3FF]/60 text-[#54E3FF] px-3 font-bold hover:bg-[#54E3FF]/10" title="Dashboard-only key: they sign in with 16 digits, no email. Editing unlocks once they confirm an email." data-testid="admin-invite-key-create">
          <KeyRound className="h-3.5 w-3.5" /> {st.key_owner ? "New access key" : "Issue access key"}
        </button>
      )}
      {st.key_owner && !key && <span className="text-white/60" data-testid="admin-invite-key-status">key issued · {st.key_owner.login_count ? `signed in ${st.key_owner.login_count}×` : "not signed in yet"}{st.key_owner.recovery_email ? ` · email ${st.key_owner.recovery_email_verified ? "confirmed" : "pending"}` : " · dashboard only"}</span>}
    </>
  );

  if (st.verified_owner && !st.invite) {
    return (
      <div className="bg-emerald-50 border-b border-emerald-200 text-emerald-900 text-xs sm:text-sm px-4 py-2 flex flex-wrap items-center justify-center gap-2" data-testid="admin-invite-verified">
        <UserCheck className="h-4 w-4" /> Owner verified: <b>{st.verified_owner}</b> — they see "Your page" + Manage on their dashboard.
        <button type="button" onClick={mint} disabled={busy} className="underline text-emerald-800 hover:text-emerald-950" data-testid="admin-invite-force">{busy ? "Creating…" : "Send a dedicated invite anyway"}</button>
      </div>
    );
  }
  const inv = st.invite;
  return (
    <div className="bg-[#1A1A1A] text-white text-xs sm:text-sm px-4 py-2.5" data-testid="admin-invite-bar">
      <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-center gap-2">
        <span className="font-bold text-[#F2CC8F]">Admin · {st.verified_owner ? `dedicated invite for ${st.name}` : `no verified owner for ${st.name}`}.</span>
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="their email (optional — locks the invite to it)" className="h-8 rounded-full bg-white/10 border border-white/20 px-3 text-xs text-white placeholder:text-white/50 w-64" data-testid="admin-invite-email" />
        {inv ? (
          <>
            <button type="button" onClick={() => copy(inv.message)} className="inline-flex items-center gap-1.5 h-8 rounded-full bg-[#F2CC8F] text-[#120A24] px-3 font-bold hover:bg-[#e6b96f]" data-testid="admin-invite-copy">
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Copy invite message
            </button>
            <button type="button" onClick={() => copy(inv.link)} className="inline-flex items-center gap-1.5 h-8 rounded-full border border-white/30 px-3 hover:bg-white/10" data-testid="admin-invite-copy-link"><Link2 className="h-3.5 w-3.5" /> Link only</button>
            <span className="text-white/60">code {inv.code} · expires {new Date(inv.expires_at).toLocaleDateString()}</span>
            <button type="button" onClick={mint} disabled={busy} className="inline-flex items-center gap-1 text-white/70 hover:text-white underline" data-testid="admin-invite-regen"><RefreshCw className="h-3 w-3" /> new link</button>
          </>
        ) : (
          <button type="button" onClick={mint} disabled={busy} className="inline-flex items-center gap-1.5 h-8 rounded-full bg-[#F2CC8F] text-[#120A24] px-3 font-bold hover:bg-[#e6b96f]" data-testid="admin-invite-create">
            <Copy className="h-3.5 w-3.5" /> {busy ? "Creating…" : "Create & copy invite"}
          </button>
        )}
        <span className="text-white/40">or</span>
        {keyRow}
      </div>
    </div>
  );
};
